<template>
    <base-layout v-if="detail.data.id" :theme="detail.data.theme">
        <template slot="sidebar">
            <space-sidebar :space="detail.data" class="sidebar-content hide-scroll-bar"/>
        </template>

        <div class="column is-paddingless">
            <div class="content-section">
                <div class="is-clearfix">
                    <h1 class="title is-pulled-left">
                        Flow: {{flow ? flow.name : ''}}
                    </h1>

                    <user-avatar class="is-pulled-right"/>
                    <column-create v-if="flow" :space="detail.data" :flow="flow"/>
                    <div class="search">
                        <base-input
                            v-model="query"
                            @keypress.enter="search"
                            placeholder="Search..."
                            icon="fas fa-search"
                            name="search"/>
                    </div>

                    <tag-picker
                        :space="detail.data"
                        :exclude="filter_tag_ids"
                        @select="select_tag"
                        class="tag-picker">
                        <i class="fas fa-tag icon"></i>
                    </tag-picker>

                    <div v-if="filter_tags.length" class="tags-list">
                        Tags: &nbsp;
                        <tag v-for="tag in filter_tags"
                             @remove="remove_tag(tag)"
                             :show_remove="true"
                             :color="tag.color"
                             :name="tag.name"
                             :key="tag.id"/>
                    </div>
                </div>

                <div class="is-clearfix"></div>

                <!-- TODO: show all errors (columns, cards) -->
                <base-backend-errors :data="update.error"/>
            </div>

            <draggable
                v-if="!columns_list.loading && flow"
                :class="{'small-column': selected_card}"
                :value="columns_list.data.results"
                @change="move_column"
                class="kanban-columns"
                group="columns">

                <column
                    :column="{id: 'open', name: 'Open'}"
                    :space="detail.data"
                    :editable="false"
                    :flow="flow"
                    :show_icon="false"
                    :search="search_query"
                    :filter_tags="filter_tag_ids"
                    @select="card => select_card(card, 'open')"
                    color="gray"/>

                <column
                    v-for="column in columns_list.data.results"
                    :filter_tags="filter_tag_ids"
                    :search="search_query"
                    :key="column.id"
                    :flow="flow"
                    :column="column"
                    :space="detail.data"
                    :color="column.color"
                    @select="card => select_card(card, column.id)"/>

                <column
                    :column="{id: 'close', name: 'Close'}"
                    :space="detail.data"
                    :editable="false"
                    :flow="flow"
                    :show_icon="false"
                    :search="search_query"
                    :filter_tags="filter_tag_ids"
                    @select="card => select_card(card, 'close')"
                    color="gray"/>
            </draggable>

            <p v-else align="center">
                <br/>
                <span class="loader title is-1"></span>
            </p>
        </div>

        <card-details
            v-if="selected_card"
            :space="detail.data"
            :column="selected_card_column"
            :card="selected_card"
            @close="close_right_panel"
            @removed="removed"
            class="right-sidebar"/>
    </base-layout>

    <h1 v-else class="loader-container has-text-centered">
        <span class="loader is-inline-flex"></span>
    </h1>
</template>

<script>
import _map from 'lodash/map';
import _reject from 'lodash/reject';
import { mapState, mapGetters } from 'vuex';
import Draggable from 'vuedraggable/src/vuedraggable';
import ColumnCreate from '../components/flows/ColumnCreate.vue';
import SpaceSidebar from '../components/space/SpaceSidebar.vue';
import CardDetails from '../components/flows/CardDetails.vue';
import BaseLayout from '../components/layouts/BaseLayout.vue';
import UserAvatar from '../components/user/UserAvatar.vue';
import TagPicker from '../components/tag/TagPicker.vue';
import Column from '../components/flows/Column.vue';
import Tag from '../components/tag/Tag.vue';
import { set_title } from '../utils/helpers';

export default {
    name: 'SpacesFlow',
    components: {
        Tag,
        TagPicker,
        CardDetails,
        ColumnCreate,
        SpaceSidebar,
        UserAvatar,
        BaseLayout,
        Column,
        Draggable,
    },
    computed: {
        ...mapState('spaces', ['detail', 'update']),
        ...mapState('columns', { columns_list: 'list' }),
        ...mapGetters('flows', { get_flow_by_id: 'by_id' }),
        flow() {
            return this.get_flow_by_id(parseInt(this.$route.params.flow, 10));
        },
        filter_tag_ids() {
            return _map(this.filter_tags, 'id');
        },
    },
    data() {
        return {
            selected_card_column: null,
            selected_card: null,
            filter_tags: [],
            search_query: '',
            query: '',
        };
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                this.$store.commit('spaces/set_last_page', {
                    space_id: this.$route.params.space,
                    url: this.$route.fullPath,
                });
            },
        },
    },
    async created() {
        const flow_id = this.$route.params.flow;
        const space_id = this.$route.params.space;

        this.$store.dispatch('columns/list', { space_id, flow_id });
        await this.$store.dispatch('spaces/detail', { space_id });
        set_title(this.detail.data.name);
    },
    methods: {
        select_card(card, column) {
            if (this.selected_card === card && this.selected_card_column === column) {
                // Close panel if user click card twice
                this.selected_card = null;
                this.selected_card_column = null;
                return;
            }
            this.selected_card = card;
            this.selected_card_column = column;
        },
        close_right_panel() {
            this.selected_card = null;
            this.selected_card_column = null;
        },
        removed() {
            this.$store.dispatch('cards/list', {
                space_id: this.detail.data.id,
                flow_id: this.flow.id,
                column: this.selected_card_column,
                page: 1,
            });

            this.close_right_panel();
        },
        async move_column(result) {
            const to = result.moved.newIndex - 1;
            const from = result.moved.oldIndex - 1;
            const column = this.columns_list.data.results[from];
            const total = this.columns_list.data.results.length;

            // Avoid moving before 'open' and after 'close' columns
            if (to < 0 || to === total || !column) {
                return;
            }

            this.$store.commit('columns/move_column', { from, to });
            await this.$store.dispatch('columns/update', {
                space_id: this.detail.data.id,
                flow_id: this.flow.id,
                column_id: column.id,
                data: { position: to },
            });
        },
        search() {
            this.search_query = this.query;
        },
        select_tag(tag) {
            this.filter_tags.push(tag);
        },
        remove_tag(tag) {
            this.filter_tags = _reject(this.filter_tags, tag);
        },
    },
};

</script>

<style scoped>
    .content-section {
        overflow: visible;
    }
    .kanban-columns {
        height: calc(100vh - 7rem);
        width: calc(100vw - 16rem);
        overflow-x: scroll;
        overflow-y: hidden;
        display: flex !important;
    }

    .kanban-columns.small-column {
        /* Make horizontal scroll container smaller to avoid overlapping with right sidebar */
        width: calc(100vw - 41rem);
    }

    .right-sidebar {
        position: absolute;
        right: 0;
    }

    .search {
        float: right;
        width: 15rem;
        margin-right: 1rem;
    }

    .tag-picker {
        margin: 0.3rem 1rem;
        float: right;
    }

    .tag-picker .icon {
        font-size: 1.3em;
    }

    .tags-list {
        float: right;
        margin: 0.2rem;
    }
</style>
